import React from "react";
import { TFunction } from "i18next";
import { customColumnProps } from "@maherunlocker/custom-react-table";

import CustomColumnDisplay from "@components/Common/CustomColumnDisplay";
import {
    DUPLICATION_OPERATIONS,
    HISTORY_ACTION_ITEM,
    HISTORY_I18N_KEY,
    HISTORY_OPERATION,
} from "@constants/index";

export type HistoryAction = {
    item: string;
    oldValue?: string;
    newValue?: string;
};

export type HistoryRow = {
    operation: string;
    actions: HistoryAction[];
    [key: string]: unknown;
};

type CustomJsxArg = { selectedRow: HistoryRow };

const ACTION_ITEM_LABELS: Record<string, string> = {
    [HISTORY_ACTION_ITEM.DESIGNATION]: HISTORY_I18N_KEY.DESIGNATION,
    [HISTORY_ACTION_ITEM.NAME]: HISTORY_I18N_KEY.DESIGNATION_FR,
    [HISTORY_ACTION_ITEM.NOTE]: HISTORY_I18N_KEY.REMARK,
};

const SIMPLE_OPERATION_LABEL: Record<string, string> = {
    [HISTORY_OPERATION.CREATION]: HISTORY_I18N_KEY.CREATION,
    [HISTORY_OPERATION.CONFIGURATION_TEMPLATE]:
        HISTORY_I18N_KEY.CONFIGURATION_TEMPLATE,
    [HISTORY_OPERATION.ARCHIVAGE]: HISTORY_I18N_KEY.ARCHIVING,
    [HISTORY_OPERATION.DESARCHIVAGE]: HISTORY_I18N_KEY.UNARCHIVING,
};

const RAW_PARTIAL_MATCH_KEYS = [
    HISTORY_OPERATION.CONFIGURATION_TEMPLATE,
    HISTORY_OPERATION.DISSOCIATION,
    HISTORY_OPERATION.ASSOCIATES,
];

const EMPTY_CELL = <span>{""}</span>;

const isCreationLike = (operation: string) =>
    operation === HISTORY_OPERATION.CREATION ||
    DUPLICATION_OPERATIONS.includes(operation);

const display = (text: string) => <CustomColumnDisplay text={text} />;

const buildEditedFieldsText = (actions: HistoryAction[], t: TFunction) =>
    actions
        .map((a) => ACTION_ITEM_LABELS[a.item])
        .filter(Boolean)
        .map((key) => t(key))
        .join(",");

const buildArchiveStatusText = (
    t: TFunction,
    statusKey: string,
    leadingSpace = true
) => `${leadingSpace ? " " : ""}${t(HISTORY_I18N_KEY.STATUS)}: ${t(statusKey)}`;

const renderActionsCell = (row: HistoryRow, t: TFunction): React.ReactNode => {
    const { operation, actions } = row;

    if (DUPLICATION_OPERATIONS.includes(operation)) {
        return display(t(HISTORY_I18N_KEY.DUPLICATION));
    }

    if (operation === HISTORY_OPERATION.MODIFY) {
        return display(
            `${t(HISTORY_I18N_KEY.EDIT)}:${buildEditedFieldsText(actions, t)}`
        );
    }

    const simpleLabel = SIMPLE_OPERATION_LABEL[operation];
    if (simpleLabel) return display(t(simpleLabel));

    if (RAW_PARTIAL_MATCH_KEYS.some((key) => operation.includes(key))) {
        return display(operation);
    }

    if (operation.includes(HISTORY_OPERATION.PUBLICATION)) {
        return display(t(HISTORY_I18N_KEY.PUBLICATION));
    }

    return EMPTY_CELL;
};

const renderBeforeEditCell = (
    row: HistoryRow,
    t: TFunction
): React.ReactNode => {
    const { operation, actions } = row;

    if (isCreationLike(operation)) return null;

    if (
        operation === HISTORY_OPERATION.MODIFY ||
        operation === HISTORY_OPERATION.PUBLICATION
    ) {
        return display(
            actions
                .filter((a) => a.item !== HISTORY_ACTION_ITEM.MODIFIED_AT)
                .map((a) => a.oldValue ?? "")
                .join(",")
        );
    }

    if (operation === HISTORY_OPERATION.DESARCHIVAGE) {
        return display(buildArchiveStatusText(t, HISTORY_I18N_KEY.ARCHIVED));
    }

    if (operation === HISTORY_OPERATION.ARCHIVAGE) {
        return display(buildArchiveStatusText(t, HISTORY_I18N_KEY.UNARCHIVED));
    }

    return EMPTY_CELL;
};

const renderAfterEditCell = (
    row: HistoryRow,
    t: TFunction
): React.ReactNode => {
    const { operation, actions } = row;

    if (isCreationLike(operation)) {
        const designation = row[t(HISTORY_I18N_KEY.DESIGNATION)];
        return display(designation !== undefined ? String(designation) : "--");
    }

    if (
        operation === HISTORY_OPERATION.MODIFY ||
        operation === HISTORY_OPERATION.PUBLICATION
    ) {
        return display(actions.map((a) => a.newValue ?? "").join(","));
    }

    if (operation === HISTORY_OPERATION.DESARCHIVAGE) {
        return display(
            buildArchiveStatusText(t, HISTORY_I18N_KEY.UNARCHIVED, false)
        );
    }

    if (operation === HISTORY_OPERATION.ARCHIVAGE) {
        return display(buildArchiveStatusText(t, HISTORY_I18N_KEY.ARCHIVED));
    }

    return EMPTY_CELL;
};

export const useHistoryColumns = (t: TFunction): customColumnProps[] =>
    React.useMemo(
        () => [
            {
                indexOFColumn: 1,
                columnName: t(HISTORY_I18N_KEY.ACTIONS),
                customJsx: ({ selectedRow }: CustomJsxArg) =>
                    renderActionsCell(selectedRow, t),
            },
            {
                indexOFColumn: 2,
                columnName: t(HISTORY_I18N_KEY.BEFORE_EDIT),
                customJsx: ({ selectedRow }: CustomJsxArg) =>
                    renderBeforeEditCell(selectedRow, t),
            },
            {
                indexOFColumn: 3,
                columnName: t(HISTORY_I18N_KEY.AFTER_EDIT),
                customJsx: ({ selectedRow }: CustomJsxArg) =>
                    renderAfterEditCell(selectedRow, t),
            },
        ],
        [t]
    );
